import { BEHIND_PAINT, BEHIND_PAINT_HEX, LINE_WEIGHTS, FOCUS_STROKE, GHOST_OPACITY, PAPER_HEX, PLATE_BEHIND_FILTER_ID, WASH_OPACITY, WASH_PAINT } from "@pushback-and-chaos/contracts";
import { generateShell, SLAB_CM } from "./shell.js";
import { wallClock, flipCalendar, timeFixtureHousing } from "./time-fixtures.js";
import { mirrorTransform } from "./mirror.js";
import { el } from "./svg-model.js";
const FOCUS_SUFFIX = "--focus";
/** A standing or seated figure's head and shoulders fit a square this many centimetres on a side, from the top. */
const CALL_CROP_CM = 62;
const CALL_HANDSET = "ink.shared.a14-office-devices.phone";
const DRAWABLE_TAG = /<(path|line|polyline|polygon|rect|circle|ellipse)\b([^>]*)>/g;
/**
 * The focus variant of a shipped symbol: same geometry, same occlusion
 * layer, every ink or accent stroke at FOCUS_STROKE, nested figure
 * references pointed at THEIR focus variants. A pure string transform over
 * validated markup (presentation attributes only, by contract).
 */
export function focusVariant(markup, id) {
    // Masks need their own IDs when base and focused instances share a document.
    const localIds = [...markup.matchAll(/<(?:clipPath|filter)\b[^>]*id="([^"]+)"/gu)].map((match) => match[1]).filter((value) => value !== undefined);
    let focused = markup;
    for (const local of localIds)
        focused = focused.replaceAll(`id="${local}"`, `id="${local}${FOCUS_SUFFIX}"`).replaceAll(`url(#${local})`, `url(#${local}${FOCUS_SUFFIX})`);
    return focused
        .replace(`id="${id}"`, `id="${id}${FOCUS_SUFFIX}"`)
        .replace(/href="#([^"]+)"/g, (_m, ref) => `href="#${ref}${FOCUS_SUFFIX}"`)
        .replace(DRAWABLE_TAG, (whole, tag, attrs) => {
        if (!/stroke="(currentColor|var\(--pc-string[^"]*)"/.test(attrs))
            return whole;
        const retoned = /stroke-width="[^"]*"/.test(attrs)
            ? attrs.replace(/stroke-width="[^"]*"/, `stroke-width="${String(FOCUS_STROKE)}"`)
            : `${attrs} stroke-width="${String(FOCUS_STROKE)}"`;
        return `<${tag}${retoned}>`;
    });
}
export function composeSection(input) {
    const { definition, view, symbols } = input;
    const shell = generateShell(definition);
    const m = shell.metrics;
    const usedBase = new Set();
    const usedFocus = new Set();
    let usedPlateBehind = false;
    let usedIllustrationBehind = false;
    const body = [];
    const treatment = input.treatment ?? "day";
    const backplate = (input.backplates ?? []).find((b) => b.treatment === treatment) ?? null;
    let skyMask = null;
    if (backplate !== null) {
        body.push(el("g", { "data-layer": "backplate" }, [
            el("image", { href: backplate.href, x: fmt(backplate.frame.x), y: fmt(backplate.frame.y), width: fmt(backplate.frame.w), height: fmt(backplate.frame.h), preserveAspectRatio: "none" }),
        ]));
    }
    // ---- room tones: projected weight (discovery) over authored depth ----
    const roomWeight = new Map();
    for (const w of view.weights)
        roomWeight.set(w.targetRef, w.weight);
    const toneOf = (room) => {
        if (input.cameraRoom === room.id)
            return "focus";
        if (room.depth === "behind")
            return "behind";
        return (roomWeight.get(room.id) ?? room.initialWeight) === "ghost" ? "ghost" : "focus";
    };
    const roomById = new Map(definition.rooms.map((r) => [r.id, r]));
    // ---- sky: clouds and birds drifting BEHIND the building (shell.sky).
    // Deterministic placement; drift is client animation, transform only. ----
    if (definition.shell.sky !== null) {
        const sky = [];
        const { cloudRefs, birds } = definition.shell.sky;
        const focused = definition.shell.focus !== null;
        // In the focused mid-building framing there is no open sky above the
        // roof — the only sky the crop can see is the two side margins (the
        // alley gap and the tree side). Placing drifters across the full span
        // parks them behind the building mask, permanently invisible (found on
        // the floor-4 crop). So under focus, each drifter starts hidden at a
        // margin's inner edge and its drift span carries it across that margin;
        // the wrap happens off-screen (behind the mask or past the crop edge).
        const rightStrip = { from: m.buildingW, w: m.viewBox.x + m.viewBox.w - m.buildingW };
        const leftStrip = { from: m.viewBox.x, w: -m.viewBox.x };
        const place = (ref, kind, index, y) => {
            const sprite = symbols.get(ref);
            if (sprite === undefined)
                return;
            usedBase.add(ref);
            const fp = sprite.meta.footprint;
            let x;
            let span;
            if (focused) {
                // Clouds keep to the open tree side; birds alternate sides.
                const strip = kind === "bird" && index % 2 === 1 ? leftStrip : rightStrip;
                x = strip.from - fp.w;
                span = strip.w + fp.w * 2;
            }
            else {
                span = m.viewBox.w;
                x = m.viewBox.x + span * ((index + 0.5) / (cloudRefs.length + birds + 0.4));
            }
            sky.push(el("g", { "data-ambient": kind, "data-drift-span": fmt(span), "data-drift-seed": String(kind === "bird" ? index + 3 : index) }, [
                el("use", { href: `#${ref}`, x: fmt(x), y: fmt(y), width: fmt(fp.w), height: fmt(fp.h) }),
            ]));
        };
        cloudRefs.forEach((ref, index) => {
            place(ref, "cloud", index, m.viewBox.y + 40 + (index % 3) * 130);
        });
        for (let index = 0; index < birds; index += 1) {
            place("core.ext.bird", "bird", index, m.viewBox.y + 110 + index * 90);
        }
        // The building masks the sky: rooms are unfilled line work, so without
        // this an occluding cloud would drift THROUGH the bullpen. One mask per
        // wing — a courtyard is open to the sky. Over a backplate the painting is
        // opaque, so paper rects would hide it: the same wing boxes become an SVG
        // mask on the sky group instead, and the drifters simply vanish behind
        // the painted building.
        const wingBoxes = m.wings.map((wing) => {
            const top = m.floorY(wing.floors) - 40;
            return { x: fmt(wing.x0), y: fmt(top), width: fmt(wing.x1 - wing.x0), height: fmt(-top + 35) };
        });
        if (backplate === null) {
            body.push(el("g", { "data-layer": "sky", opacity: "0.34" }, sky));
            for (const box of wingBoxes)
                body.push(el("rect", { ...box, fill: "var(--pc-grid-bg, #F2F2F2)", stroke: "none" }));
        }
        else {
            skyMask = el("mask", { id: "pc-sky-mask", maskUnits: "userSpaceOnUse", x: fmt(m.viewBox.x), y: fmt(m.viewBox.y), width: fmt(m.viewBox.w), height: fmt(m.viewBox.h) }, [
                el("rect", { x: fmt(m.viewBox.x), y: fmt(m.viewBox.y), width: fmt(m.viewBox.w), height: fmt(m.viewBox.h), fill: "white" }),
                ...wingBoxes.map((box) => el("rect", { ...box, fill: "black" })),
            ]);
            body.push(el("g", { "data-layer": "sky", opacity: "0.34", mask: "url(#pc-sky-mask)" }, sky));
        }
    }
    // ---- window light: a pure function of calendar.lightAngle (grammar),
    // falling into the lower FOCUS floor (the bullpen), not the basement ----
    const lightFloor = definition.shell.focus?.fromFloor ?? 0;
    body.push(windowLight(view.calendar.lightAngle, m.buildingW, m.clearH, m.floorY(lightFloor)));
    // ---- PAINT ORDER IS DEPTH (ADR 0010): everything behind the plane — the
    // courtyard core and the rooms seen through openings — lies UNDER the
    // black-stroke shell, so a behind symbol's paper silhouette (the courtyard
    // tree) can hide the core without ever covering a cut line or anything in
    // the plane. The behind layer is emitted here, once the rooms are placed. ----
    const behindShell = shell.nodes.filter((n) => n.attrs["data-depth"] === "behind");
    const planeShell = shell.nodes.filter((n) => n.attrs["data-depth"] !== "behind");
    const behindLayerIndex = body.length;
    body.push(el("g", { "data-layer": "behind" }, [])); // filled in below
    // Over a backplate the painted building replaces the generated line work.
    body.push(el("g", { "data-layer": "shell" }, backplate === null ? planeShell : []));
    // Room children carry a z rank (back 0 / mid 1 / front 2, from the
    // symbol's declared zband) and are emitted back-to-front: with the
    // occlusion layer in every symbol, PAINT ORDER IS DEPTH — a figure must
    // paint after the wall board behind it or the board erases the figure.
    const roomChildren = new Map();
    const childrenOf = (roomId) => {
        const existing = roomChildren.get(roomId);
        if (existing !== undefined)
            return existing;
        const created = [];
        roomChildren.set(roomId, created);
        return created;
    };
    const zRank = (zband) => (zband === "back" ? 0 : zband === "front" ? 2 : 1);
    const resolveSlot = (roomId, index) => {
        const room = roomById.get(roomId);
        if (room === undefined)
            return null;
        const slot = room.slots[index] ?? room.slots[index % Math.max(room.slots.length, 1)];
        if (slot === undefined)
            return null;
        return { x: slot.x, y: m.floorY(room.floor) - slot.y };
    };
    // A POSE VARIANT: an actor whose pack ships `<symbolRef>-<pose>` draws that
    // symbol (a cast member standing at the threshold, seated at the desk);
    // otherwise the base symbol — so a pose the packs do not know yet never
    // blanks a character. Stations author poses; the projection carries them;
    // the packs decide what they look like.
    const place = (roomId, slotIdx, symbolRef, facing, extraAttrs, variant, pose = null, contact) => {
        const spot = contact ?? resolveSlot(roomId, slotIdx);
        if (spot === null)
            return;
        const posed = pose !== null && symbols.has(`${symbolRef}-${pose}`) ? `${symbolRef}-${pose}` : symbolRef;
        const variantRef = variant !== null && symbols.has(`${posed}-${variant}`) ? `${posed}-${variant}` : posed;
        const sprite = symbols.get(variantRef);
        if (sprite === undefined)
            return; // missing symbols are a validator error, never a draw-time crash
        const room = roomById.get(roomId);
        const tone = room === undefined ? "ghost" : toneOf(room);
        const focus = tone === "focus";
        if (focus)
            usedFocus.add(variantRef);
        else
            usedBase.add(variantRef);
        const plateBehind = tone === "behind" && sprite.meta.plate !== undefined;
        const illustratedBehind = plateBehind && sprite.meta.illustration !== undefined;
        if (illustratedBehind)
            usedIllustrationBehind = true;
        else if (plateBehind)
            usedPlateBehind = true;
        const { footprint, anchor } = sprite.meta;
        const flipped = sprite.meta.illustration === undefined ? facing === "left" : sprite.meta.illustration.mirroring === "safe" && sprite.meta.illustration.nativeFacing !== "none" && facing !== sprite.meta.illustration.nativeFacing;
        // Mirrored modular actors keep their measured ground contact at the station.
        const anchorX = flipped && input.contactRooms?.has(roomId) === true ? footprint.w - anchor.x : anchor.x;
        const x = spot.x - anchorX;
        const y = spot.y - (contact?.topGrip === true ? footprint.h * .08 : anchor.y);
        // width/height are MANDATORY on <use> of a <symbol>: without them the
        // symbol scales to 100% of the viewport (every instance stretched to
        // the whole drawing — found on the first render).
        const useNode = el("use", {
            href: `#${variantRef}${focus ? FOCUS_SUFFIX : ""}`,
            x: fmt(x),
            y: fmt(y),
            width: fmt(footprint.w),
            height: fmt(footprint.h),
            ...extraAttrs,
            ...(variant !== null ? { "data-variant": variant } : {}),
            ...(plateBehind ? { filter: `url(#${illustratedBehind ? "pc-illustration-behind" : PLATE_BEHIND_FILTER_ID})` } : {}),
        });
        // A CONTACT SHADOW under anything that stands in the room plane: a soft
        // ink ellipse at the anchor, so a plate reads as standing on the floor
        // rather than pinned to it. Back-band scenery and fixtures get none.
        if (sprite.meta.zband !== "back" && extraAttrs["data-fixture"] === undefined && (sprite.meta.illustration === undefined || sprite.meta.illustration.shadow === "runtime")) {
            childrenOf(roomId).push({
                rank: zRank(sprite.meta.zband) - 0.5,
                node: el("ellipse", { cx: fmt(spot.x), cy: fmt(spot.y - 2), rx: fmt(Math.max(12, footprint.w * 0.42)), ry: "5", fill: "currentColor", stroke: "none", opacity: "0.13", "data-part": "shadow" }),
            });
        }
        childrenOf(roomId).push({
            rank: contact === undefined ? zRank(sprite.meta.zband) : 3,
            node: flipped ? el("g", { transform: mirrorTransform(x, footprint.w) }, [useNode]) : useNode,
        });
    };
    // ---- props (facing comes from the authored definition: the view carries placement, not orientation) ----
    for (const prop of view.props) {
        const room = roomById.get(prop.room);
        const slotId = room?.slots[prop.at]?.id;
        const authored = definition.props.find((p) => p.room === prop.room && p.slot === slotId && p.symbolRef === prop.symbolRef);
        place(prop.room, prop.at, prop.symbolRef, authored?.facing ?? "right", {}, prop.variant);
    }
    // ---- fixtures: placed from the definition, stated from the view ----
    for (const fixture of view.fixtures) {
        const def = definition.fixtures.find((fx) => fx.id === fixture.id);
        if (def === undefined)
            continue;
        const room = roomById.get(def.room);
        const slotIdx = room?.slots.findIndex((s) => s.id === def.slot) ?? -1;
        if (room === undefined || slotIdx < 0)
            continue;
        const housing = timeFixtureHousing(fixture.symbolRef);
        // Recovery drops plate metadata; retain exact hands/date over a network-free housing.
        const painted = housing !== null && symbols.get(housing)?.meta.plate !== undefined;
        if (painted && housing !== null)
            place(room.id, slotIdx, housing, "right", { "data-fixture-housing": fixture.id }, null);
        if (fixture.symbolRef === "core.fx.clock") {
            const spot = resolveSlot(room.id, slotIdx);
            if (spot !== null)
                childrenOf(room.id).push({ rank: 0, node: wallClock(fixture.id, spot.x, spot.y, view.clock.hour, view.clock.minute, painted) });
            continue;
        }
        if (fixture.symbolRef === "core.fx.calendar") {
            const spot = resolveSlot(room.id, slotIdx);
            if (spot !== null)
                childrenOf(room.id).push({ rank: 0, node: flipCalendar(fixture.id, spot.x, spot.y, view.calendar.weekdayLabel, view.calendar.dayNumeral, painted) });
            continue;
        }
        place(room.id, slotIdx, fixture.symbolRef, "right", {
            "data-fixture": fixture.id,
            "data-state": fixture.state,
            ...(fixture.state === "dark" || fixture.state === "off" ? { opacity: "0.35" } : {}),
        }, null);
    }
    // ---- actors (each animatable by instanceId), their carried props ----
    for (const actor of view.actors) {
        place(actor.room, actor.slot, actor.symbolRef, actor.facing, { "data-actor": actor.instanceId, "data-pose": actor.pose }, null, actor.pose);
        for (const [i, propRef] of actor.propRefs.entries()) {
            const posed = symbols.get(`${actor.symbolRef}-${actor.pose}`) ?? symbols.get(actor.symbolRef);
            const hand = posed?.meta.illustration?.contacts.find((point) => point.kind === "hand");
            const spot = resolveSlot(actor.room, actor.slot);
            let contact;
            if (input.contactRooms?.has(actor.room) === true && hand !== undefined && posed !== undefined && spot !== null) {
                const { footprint, anchor, illustration } = posed.meta;
                const flipped = illustration?.mirroring === "safe" && illustration.nativeFacing !== "none" && illustration.nativeFacing !== actor.facing;
                contact = { x: spot.x - (flipped ? footprint.w - anchor.x : anchor.x) + (flipped ? footprint.w - hand.x : hand.x) + i * 6, y: spot.y - anchor.y + hand.y - i * 3, topGrip: /carrier|bag|headset/u.test(propRef) };
            }
            place(actor.room, actor.slot, propRef, actor.facing, { "data-companion": `${actor.instanceId}-${String(i)}`, "data-held-prop": propRef }, null, null, contact);
        }
    }
    // ---- someone on the phone: a small shoulders-up inset beside whoever holds
    // the line. They are not in the room, so they are never an actor here. The
    // person's own on-the-phone portrait (`<their id>-call`) is used when it is
    // painted; until then their standing painting is cropped to the shoulders and
    // marked with a handset. An unknown painting or an absent holder draws nothing.
    if (view.call !== undefined) {
        const call = view.call;
        const holder = view.actors.find((actor) => actor.instanceId === call.with);
        const spot = holder === undefined ? null : resolveSlot(holder.room, holder.slot);
        const room = holder === undefined ? undefined : roomById.get(holder.room);
        const painted = symbols.has(`${call.symbolRef}-call`);
        const portraitId = painted ? `${call.symbolRef}-call` : call.symbolRef;
        const portrait = symbols.get(portraitId);
        if (holder !== undefined && spot !== null && room !== undefined && portrait !== undefined) {
            const focus = toneOf(room) === "focus";
            const refOf = (id) => { (focus ? usedFocus : usedBase).add(id); return `#${id}${focus ? FOCUS_SUFFIX : ""}`; };
            const BOX = 66, PAD = 4, inner = BOX - PAD * 2;
            // Head and shoulders: the top of a standing or seated figure, as a square centred on the feet.
            const crop = painted ? { x: 0, y: 0, w: portrait.meta.footprint.w, h: portrait.meta.footprint.h } : { x: portrait.meta.anchor.x - CALL_CROP_CM / 2, y: 0, w: CALL_CROP_CM, h: CALL_CROP_CM };
            const holderHeight = holder.pose === "sit" ? 132 : 175;
            const span = m.roomSpan(room), left = span.x0 + 10, right = span.x1 - 10;
            const beside = spot.x + 34 + BOX <= right ? spot.x + 34 : Math.max(left, spot.x - 34 - BOX);
            const top = spot.y - holderHeight - BOX * 0.62;
            const handset = painted ? undefined : symbols.get(CALL_HANDSET);
            childrenOf(holder.room).push({ rank: 6, node: el("g", { "data-call": call.who, "data-call-with": call.with, "data-call-portrait": painted ? "painted" : "cropped" }, [
                    el("rect", { x: fmt(beside), y: fmt(top), width: fmt(BOX), height: fmt(BOX), rx: "3", fill: "var(--pc-section-paper, #F2F2F2)", stroke: "currentColor", "stroke-width": String(LINE_WEIGHTS.obj) }),
                    el("svg", { "data-part": "call-portrait", x: fmt(beside + PAD), y: fmt(top + PAD), width: fmt(inner), height: fmt(inner), viewBox: `${fmt(crop.x)} ${fmt(crop.y)} ${fmt(crop.w)} ${fmt(crop.h)}`, preserveAspectRatio: "xMidYMin slice", overflow: "hidden" }, [
                        el("use", { href: refOf(portraitId), x: "0", y: "0", width: fmt(portrait.meta.footprint.w), height: fmt(portrait.meta.footprint.h) }),
                    ]),
                    ...(handset === undefined ? [] : [el("use", { href: refOf(CALL_HANDSET), x: fmt(beside + BOX - PAD - 9), y: fmt(top + BOX - PAD - 17), width: fmt(handset.meta.footprint.w), height: fmt(handset.meta.footprint.h), "data-part": "call-handset" })]),
                    // A short tick from the box toward the person holding the line.
                    el("path", { d: `M${fmt(beside < spot.x ? beside + BOX : beside)} ${fmt(top + BOX - 10)} L${fmt(spot.x + (beside < spot.x ? -12 : 12))} ${fmt(top + BOX + 8)}`, fill: "none", stroke: "currentColor", "stroke-width": String(LINE_WEIGHTS.det), "stroke-linecap": "round" }),
                ]) });
        }
    }
    // ---- emit room groups with their tones: behind rooms into the behind
    // layer (under the shell), plane rooms over it ----
    // THE ROOM WASH (ADR 0015 step 3): one WASH_PAINT rectangle per room
    // volume in the plane, under the room's contents, so glazed floors read as
    // lit interiors against the paper the way the approved boards do. One per
    // bay span (a lobby and the pavement outside it share one), none inside a
    // courtyard (open air), none behind the plane (that layer is a tone).
    const washedSpans = new Set();
    const washFor = (room) => {
        if (room.depth === "behind")
            return null;
        const span = m.roomSpan(room);
        const spanKey = `${String(span.x0)}-${String(span.x1)}-${String(room.floor)}`;
        if (washedSpans.has(spanKey))
            return null;
        if (m.courtyards.some((c) => span.x0 >= c.x0 && span.x1 <= c.x1))
            return null;
        washedSpans.add(spanKey);
        const yTop = room.floor < 0 ? m.floorY(room.floor + 1) + SLAB_CM : m.floorY(room.floor) - m.clearH;
        const height = m.floorY(room.floor) - yTop;
        if (height <= 0)
            return null;
        return el("rect", { x: fmt(span.x0), y: fmt(yTop), width: fmt(span.x1 - span.x0), height: fmt(height), fill: WASH_PAINT, stroke: "none", opacity: String(WASH_OPACITY), "data-part": "wash" });
    };
    const veilFor = (room) => {
        const span = m.roomSpan(room);
        const yTop = room.floor < 0 ? m.floorY(room.floor + 1) + SLAB_CM : m.floorY(room.floor) - m.clearH;
        const height = m.floorY(room.floor) - yTop;
        if (height <= 0)
            return null;
        // Drawn inside the ghost group (opacity GHOST_OPACITY), so its own opacity is scaled: aim for ~0.55 of paper over the plate.
        return el("rect", { x: fmt(span.x0), y: fmt(yTop), width: fmt(span.x1 - span.x0), height: fmt(height), fill: PAPER_HEX, stroke: "none", opacity: "1", "data-part": "veil" });
    };
    const behindRooms = [];
    for (const room of definition.rooms) {
        const children = (roomChildren.get(room.id) ?? []).sort((a, b) => a.rank - b.rank).map((c) => c.node);
        const tone = toneOf(room);
        if (tone === "behind") {
            behindRooms.push(el("g", { "data-room": room.id, "data-depth": "behind", color: BEHIND_PAINT }, children));
            continue;
        }
        // Over a backplate the wash is painted already; a ghost room instead gets
        // a paper veil over the plate so it recedes the way the vector shell's
        // faded strokes do, with its contents faded above it as before.
        const wash = backplate === null ? washFor(room) : (tone === "ghost" ? veilFor(room) : null);
        body.push(el("g", { "data-room": room.id, ...(tone === "ghost" ? { opacity: String(GHOST_OPACITY) } : {}) }, wash === null ? children : [wash, ...children]));
    }
    body[behindLayerIndex] = el("g", { "data-layer": "behind" }, [...(backplate === null && input.cameraRoom === undefined ? behindShell : []), ...behindRooms]);
    // ---- room labels, drafting style. One label per bay span: two rooms
    // sharing bays (a lobby and the pavement outside it) must not
    // superimpose their labels into soup. ----
    const labels = [];
    const labelledSpans = new Set();
    for (const room of definition.rooms) {
        // Faded neighbor floors keep their anonymity: labels belong to OUR
        // rooms only — the focus stays on the office where the events happen.
        // A room behind the plane is seen, not entered; it gets no label.
        if (room.initialWeight === "ghost" || room.depth === "behind")
            continue;
        const spanKey = `${String(room.bays.from)}-${String(room.bays.to)}-${String(room.floor)}`;
        if (labelledSpans.has(spanKey))
            continue;
        labelledSpans.add(spanKey);
        const span = m.roomSpan(room);
        labels.push(el("text", {
            x: fmt((span.x0 + span.x1) / 2),
            // Below the service-void line (floorY - clearH + serviceVoid + a
            // clearance), so no shell line crosses the letterforms.
            y: fmt(m.floorY(room.floor) - m.clearH + 78),
            "text-anchor": "middle",
            fill: "currentColor",
            stroke: "none",
            "font-size": "20",
            "letter-spacing": "0.14em",
            opacity: "0.55",
            "font-family": "Museo, ui-sans-serif, system-ui, sans-serif",
        }, [], room.label.toUpperCase()));
    }
    body.push(el("g", { "data-layer": "labels" }, labels));
    // ---- slot markers (ADR 0014): every authored slot as an invisible,
    // zero-radius point the client's plan playback reads positions from
    // (`data-slot="room/slot"`), so a travel step along named places needs
    // no geometry of its own. Draws nothing; carries no state. ----
    const markers = [];
    for (const room of definition.rooms) {
        room.slots.forEach((slot, index) => {
            const spot = resolveSlot(room.id, index);
            if (spot === null)
                return;
            markers.push(el("circle", { "data-slot": `${room.id}/${slot.id}`, cx: fmt(spot.x), cy: fmt(spot.y), r: "0", fill: "none", stroke: "none" }));
        });
    }
    body.push(el("g", { "data-layer": "slots", "aria-hidden": "true" }, markers));
    // ---- defs: poché pattern + the symbols actually used, TRANSITIVELY:
    // a cast wrapper <use>s its base figure, and a def that references a
    // missing def renders as nothing (found on the day-7 render — Grant
    // reduced to his briefcase). Focus variants close over focus variants. ----
    const closeOver = (set) => {
        const queue = [...set];
        while (queue.length > 0) {
            const id = queue.pop();
            if (id === undefined)
                break;
            const sprite = input.symbols.get(id);
            if (sprite === undefined)
                continue;
            for (const match of sprite.markup.matchAll(/href="#([^"]+)"/g)) {
                const ref = match[1];
                if (ref !== undefined && !set.has(ref) && input.symbols.has(ref)) {
                    set.add(ref);
                    queue.push(ref);
                }
            }
        }
    };
    closeOver(usedBase);
    closeOver(usedFocus);
    const defs = [...shell.defs];
    if (skyMask !== null)
        defs.push(skyMask);
    if (usedPlateBehind)
        defs.push(plateBehindFilter());
    if (usedIllustrationBehind)
        defs.push(el("filter", { id: "pc-illustration-behind", "color-interpolation-filters": "sRGB" }, [
            el("feColorMatrix", { type: "saturate", values: "0.85" }),
            // Preserve opacity and local contrast. The old ink-to-paper mapping washed faces out.
            el("feComponentTransfer", {}, [el("feFuncR", { type: "linear", slope: "0.94" }), el("feFuncG", { type: "linear", slope: "0.94" }), el("feFuncB", { type: "linear", slope: "0.94" })]),
        ]));
    for (const id of usedBase) {
        const sprite = input.symbols.get(id);
        if (sprite !== undefined)
            defs.push({ tag: "g", attrs: {}, children: [], raw: sprite.markup });
    }
    for (const id of usedFocus) {
        const sprite = input.symbols.get(id);
        if (sprite !== undefined)
            defs.push({ tag: "g", attrs: {}, children: [], raw: focusVariant(sprite.markup, id) });
    }
    // A focused drawing must clip to its crop: SVG viewports clip to the
    // ELEMENT box, not the viewBox, so `meet` letterboxing would otherwise
    // paint the neighboring floors into the letterbox and undo the zoom.
    const rootAttrs = { "data-env": view.envId, ...(view.beat !== null ? { "data-beat": view.beat.kind, "data-beat-id": view.beat.transientId } : {}), "data-idle-phase": String(view.idlePhase) };
    if (input.definition.shell.focus !== null) {
        defs.push(el("clipPath", { id: `${view.envId.replaceAll(".", "-")}-crop` }, [
            el("rect", { x: fmt(m.viewBox.x), y: fmt(m.viewBox.y), width: fmt(m.viewBox.w), height: fmt(m.viewBox.h) }),
        ]));
        rootAttrs["clip-path"] = `url(#${view.envId.replaceAll(".", "-")}-crop)`;
    }
    return {
        viewBox: m.viewBox,
        reveal: input.definition.shell.focus === null
            ? null
            : { cropY: m.viewBox.y, cropH: m.viewBox.h, minY: m.extents.top, maxY: m.extents.bottom },
        defs,
        body: [el("g", rootAttrs, body)],
        description: view.description,
        label: view.sceneLabel,
    };
}
/** Window light: wedges from the glazed left wall, angled by the sun. Low-key, non-informational beyond time of day. */
function windowLight(lightAngle, buildingW, clearH, floorLineY) {
    const rad = ((180 - lightAngle) * Math.PI) / 180;
    const reach = Math.min(buildingW * 0.35, 900);
    const dx = Math.cos(rad) * reach;
    const y0 = floorLineY - clearH + 40;
    return el("g", { "data-layer": "window-light", opacity: "0.10" }, [
        el("path", {
            d: `M 6 ${fmt(y0)} L ${fmt(6 + Math.abs(dx))} ${fmt(floorLineY)} L ${fmt(6 + Math.abs(dx) * 0.55)} ${fmt(floorLineY)} L 6 ${fmt(y0 + 90)} Z`,
            fill: "currentColor",
            stroke: "none",
        }),
    ]);
}
function fmt(n) {
    const r = Math.round(n * 100) / 100;
    return Object.is(r, -0) ? "0" : String(r);
}
/**
 * The behind tone for a plate (ADR 0015): desaturate, then map luminance
 * from ink (0) → BEHIND_PAINT_HEX to white (1) → PAPER_HEX per channel.
 * Alpha is untouched, so the plate's transparency still occludes nothing
 * and its body still hides what stands further back.
 */
function plateBehindFilter() {
    const channel = (hex, at) => (parseInt(hex.slice(at, at + 2), 16) / 255).toFixed(3);
    const table = (at) => `${channel(BEHIND_PAINT_HEX, at)} ${channel(PAPER_HEX, at)}`;
    return el("filter", { id: PLATE_BEHIND_FILTER_ID, "color-interpolation-filters": "sRGB" }, [
        el("feColorMatrix", { type: "saturate", values: "0" }),
        el("feComponentTransfer", {}, [
            el("feFuncR", { type: "table", tableValues: table(1) }),
            el("feFuncG", { type: "table", tableValues: table(3) }),
            el("feFuncB", { type: "table", tableValues: table(5) }),
        ]),
    ]);
}
//# sourceMappingURL=compose.js.map